import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import { FC, useContext, useEffect } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import ButtonOrVideoModal from "@/components/ui/ButtonOrVideoModal/ButtonOrVideoModal";
import Image from "@/components/ui/Image/Image";
import { ModalsContext } from "@/context/ModalsContext";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import useMediaQuery from "@/hooks/useMediaQuery";
import { MediaBannerHighlightContent, MediaBannerHighlightType } from "@/types";
import cn from "@/utils/cn";

export interface Props {
  content: MediaBannerHighlightContent;
  type: MediaBannerHighlightType | string;
  wrappedText?: boolean;
  titleColor?: string;
  cardbackGroundColor?: string;
  cardbackGroundOpacity?: number;
  projectInfo?: {
    pixelStreaming?: boolean;
    pixelStreamingProjectId?: string;
  };
}

const MediaBannerHighlightCardContent: FC<Props> = ({
  content,
  type,
  wrappedText,
  projectInfo,
  titleColor,
  cardbackGroundColor,
  cardbackGroundOpacity,
}) => {
  const locale = useLocale();
  const t = useTranslations();
  const isMobile = useMediaQuery("(max-width: 600px)");
  const { setMetaverseWalkthroughModalOpen, setAdditionalInfo } = useContext(ModalsContext);
  useEffect(() => {
    setAdditionalInfo({
      pixelStreamingProjectId: projectInfo?.pixelStreamingProjectId,
    });
  }, [projectInfo?.pixelStreamingProjectId, setAdditionalInfo]);
  return (
    <div className="relative w-full h-full">
      <div
        className="absolute top-0 left-0 w-full h-full rounded-xl z-0"
        style={{
          backgroundColor: `${isMobile ? "" : cardbackGroundColor}`,
          opacity: cardbackGroundOpacity,
        }}
      />

      <div
        className={cn(
          "relative z-10 flex flex-col md:static text-center pt-[40px] md:px-l md:py-xxl text-white rounded-xl md:my-s w-full md:box-material-primary md:backdrop-blur-[100px]",
          content.logo ? "justify-between" : "justify-end",
          type == MediaBannerHighlightType.FULL_SCREEN && wrappedText && "box-material-primary backdrop-blur-[100px] h-auto static",
        )}
      >
        {content.logo && <Image className="mx-auto mb-[40px] w-[140px]" src={content.logo.url} alt={content.logo.title || ""} width={140} />}
        <div
          className={cn(
            "px-m md:px-0 pb-l md:pb-0 media-highlight-content relative",
            // (type == MediaBannerHighlightType.FULL_SCREEN || type == MediaBannerHighlightType.BOXED) &&
            //   (!content.description || (content.description && wrappedText))
            //   ? "after:!rounded-none"
            //   : "pt-3xl md:pt-0 text-left md:text-center",
            type == MediaBannerHighlightType.FULL_SCREEN && wrappedText ? "items-center" : " pt-3xl md:pt-0 text-left md:text-center",
            type == MediaBannerHighlightType.FULL_SCREEN && !wrappedText && "after:!rounded-none",
          )}
        >
          {content.title && (
            <h3
              className={`font-primary text-l  font-medium uppercase accent-font accent-title ${isMobile ? "text-center" : ""}`}
              style={{ color: titleColor ? titleColor : "" }}
            >
              {content.title}
            </h3>
          )}
          {content.subtitle && <h4 className="uppercase font-normal text-center mt-m text-xs">{content.subtitle}</h4>}
          {content.description && <p className="font-normal text-m text-center mt-[40px]">{content.description}</p>}

          <div
            className={`flex flex-col justify-center items-baseline gap-xxs mb-[${wrappedText ? "40px" : "24px"}] pb-[60px] md:pb-0`}
            style={{ alignItems: "center" }}
          >
            {/* TODO currently not implemented properly */}
            {projectInfo?.pixelStreaming ? (
              <Button
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.PRIMARY}
                theme={ButtonTheme.WHITE}
                customClass="w-full mt-m inline-flex justify-center"
                onClick={() => setMetaverseWalkthroughModalOpen(true)}
              >
                {t("button.label.metaverse")}
              </Button>
            ) : (
              content.primaryCta && (
                <ButtonOrVideoModal
                  cta={content.primaryCta}
                  btnClasses={cn(
                    "mt-[40px] md:mt-xxl inline-flex border-none justify-center md:mx-auto btn-primary-white whitespace-nowrap",
                    content.primaryCta.type !== "videoModal" && "btn btn-medium btn-primary-white shadow-strong",
                    content.secondaryCta && "w-full",
                    !(type == MediaBannerHighlightType.FULL_SCREEN && !wrappedText) && "md:mx-auto",
                  )}
                  btnSize={ButtonSize.MEDIUM}
                />
              )
            )}
            {content.secondaryCta && (
              <Button
                href={getLink(content?.secondaryCta, locale)}
                size={ButtonSize.MEDIUM}
                variant={ButtonVariant.SECONDARY}
                theme={ButtonTheme.WHITE}
                customClass="w-full mt-m inline-flex justify-center"
                onClick={() => {
                  ctaClickTracking(content?.secondaryCta?.label || "", "MediaBannerHighlightCardContent", content?.secondaryCta?.label || "");
                }}
              >
                {content.secondaryCta.label}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaBannerHighlightCardContent;
